/**
 * TimeSlotButton - Individual time slot button component
 * Displays a single bookable time slot in one of four states.
 */

import type { TimeSlotButtonProps } from './types';

export function TimeSlotButton({ slot, state, onClick }: TimeSlotButtonProps) {
  // `covered` is selected-but-not-clickable: part of the booking, but not a valid start of
  // its own. It keeps `aria-selected` (it IS part of the selection, and the segment is
  // `aria-multiselectable`) and takes `aria-disabled`.
  //
  // Visually it keeps the tint and adds `opacity-60`. The tint stays because on mobile the
  // highlighted run is the ONLY feedback for how far a booking reaches — the summary card
  // sits behind bay selection. The fade is the fix: `covered` and `selected` were pixel
  // identical apart from a cursor, which does not exist on touch, so a continuation cell
  // read as a button that did nothing when tapped.
  //
  // NB the className below is a template literal, so a block comment inside it is not a
  // comment at all — it is literal text that ships in the class attribute. Reasoning here.
  const isSelected = state === 'selected' || state === 'covered';
  const isInert = state === 'covered';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isInert}
      aria-selected={isSelected}
      aria-label={
        state === 'covered'
          ? `${slot.displayTime}, part of your booking`
          : state === 'selected'
            ? `${slot.displayTime}, selected`
            : `Book time slot ${slot.displayTime}`
      }
      aria-disabled={isInert}
      data-state={state}
      data-testid={`time-slot-button-${slot.startMinutes}`}
      className={`
        relative flex min-h-11 w-full items-center justify-center
        rounded-full border px-2 py-2 text-center transition-all sm:px-3
        focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-600 focus-visible:ring-offset-2

        border-theme-100 bg-bg-tertiary text-fg-tertiary
        data-[state=default]:text-interactive:border-theme-600
        data-[state=default]:text-interactive:bg-theme-50

        data-[state=selected]:border-theme-600 data-[state=selected]:bg-theme-50
        data-[state=selected]:text-theme-600

        data-[state=covered]:border-theme-600 data-[state=covered]:bg-theme-50
        data-[state=covered]:text-theme-600 data-[state=covered]:cursor-default
        data-[state=covered]:opacity-60

      `}
    >
      <span className="whitespace-nowrap text-sm font-semibold">
        {slot.displayTime}
      </span>
    </button>
  );
}
